import { useCallback } from 'react';
import {
  createColumnMapping,
  ExcelProcessorConfig,
  formatValidationErrors,
  parseExcelData,
  ProcessExcelResult,
  readExcelFile,
  validateRequiredFields,
  ValidationError
} from '@/lib/excel';
import { toast } from '@/hooks';

export interface ProcessOptions {
  file: File;
  sheetIndex?: number;
  maxErrors?: number;
  getUUIDByCode?: (payload: any) => Promise<any>;
  context?: any;
}

export const useExcelProcessor = <T>(config: ExcelProcessorConfig<T>) => {
  const processFile = useCallback(
    async (options: ProcessOptions): Promise<ProcessExcelResult<T>> => {
      const { file, sheetIndex = 0, maxErrors = 200, getUUIDByCode, context } = options;

      try {
        const { headers, dataRows } = await readExcelFile(file, sheetIndex);

        if (dataRows.length === 0) {
          toast.error('File Excel không có dữ liệu');
          return { success: false };
        }

        const columnMapping = createColumnMapping(headers, config.HeaderConfig);
        const parsedData = parseExcelData(columnMapping, dataRows);

        const aggregatedErrors: ValidationError[] = [];

        const requiredFieldErrors = validateRequiredFields(columnMapping, dataRows, config.RequiredFields);
        aggregatedErrors.push(...requiredFieldErrors);

        let uuidResponse: any = null;
        if (config.UUIDPayload && getUUIDByCode) {
          const payload = config.UUIDPayload(parsedData);
          uuidResponse = await getUUIDByCode(payload);

          if (!uuidResponse) {
            toast.error('File Excel không hợp lệ');
            return { success: false };
          }

          if (config.ValidateUUID) {
            const validationErrors = config.ValidateUUID(uuidResponse, columnMapping);
            aggregatedErrors.push(...validationErrors);
          }
        }

        if (aggregatedErrors.length > 0) {
          const limitedErrors = limitErrors(aggregatedErrors, maxErrors);
          return {
            success: false,
            errors: limitedErrors,
            columnMapping
          };
        }

        const data = config.Transform(dataRows, parsedData, uuidResponse, context);
        return { success: true, data, columnMapping };
      } catch (error) {
        console.error('Lỗi khi đọc file Excel:', error);
        toast.error(error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel');
        return { success: false };
      }
    },
    [config]
  );

  return { processFile, formatValidationErrors };
};

const limitErrors = (errors: ValidationError[], maxErrors: number): ValidationError[] => {
  const errorsByRow = new Map<number, ValidationError[]>();
  errors.forEach(error => {
    if (!errorsByRow.has(error.row)) {
      errorsByRow.set(error.row, []);
    }
    errorsByRow.get(error.row)!.push(error);
  });

  const limitedRows = Array.from(errorsByRow.keys()).slice(0, maxErrors);
  return limitedRows.flatMap(row => errorsByRow.get(row) || []);
};
